/**
 * SCAI central management/navigation catalog.
 * Add new management objects to SCAI_MANAGEMENTS as you share each module.
 * URLs are intentionally kept identical to the existing Enquiry and Student menus.
 *
 * Sidebar and tabs are filtered using permitted frontend_url values in localStorage.
 * `permission.action` is catalog metadata for a FUTURE role editor/API. It does NOT
 * authorize a route unless frontend guards and backend permission checks use it.
 */
const tab = (id, title, icon, path, activePaths = [path], extra = {}) => ({
  id,
  title,
  label: title,
  icon,
  path,
  activePaths,
  ...extra,
});
const bulkTools = [
  {
    id: "student-import-v1",
    title: "Import Students V1",
    path: "/students/import/v1",
    icon: "ri-upload-cloud-2-line",
    description: "Latest student import",
  },
  {
    id: "student-import-legacy",
    title: "Import Students V0",
    path: "/students/import",
    icon: "ri-upload-cloud-line",
    description: "Legacy student import",
  },
  {
    id: "student-batch-import",
    title: "Batch Enroll",
    path: "/students/import/batch-enroll/v1",
    icon: "ri-user-add-line",
    description: "Bulk batch enrollment",
  },
  {
    id: "student-discount-bulk",
    title: "Discount Offer",
    path: "/students/discount/offer/set",
    icon: "ri-price-tag-3-line",
    description: "Assign student discounts",
  },
  {
    id: "student-fee-bulk",
    title: "Fee Update",
    path: "/students/update/fee",
    icon: "ri-money-rupee-circle-line",
    description: "Bulk student fee update",
  },
  {
    id: "student-bus-fee-bulk",
    title: "Bus Fee Update",
    path: "/students/update/bus-fee",
    icon: "ri-bus-2-line",
    description: "Bulk transport fee update",
  },
  {
    id: "student-subjects-bulk",
    title: "Subjects Update",
    path: "/bulk/student/subjects/update",
    icon: "ri-book-open-line",
    description: "Bulk subject mapping",
  },
  {
    id: "student-pictures-bulk",
    title: "Profile Pictures",
    path: "/students/import/profile-picture",
    icon: "ri-image-add-line",
    description: "Import student profile photos",
  },
];
export const SCAI_MANAGEMENTS = [
  {
    key: "dashboard_management",
    title: "Dashboard",
    label: "Dashboard",
    subtitle: "School overview and quick access",
    icon: "ri-dashboard-3-line",
    sidebar: true,
    order: 0,
    defaultPath: "/",
    paths: ["/", "/dashboard/module", "/reporting/dashboard"],
    tabs: [
      tab("dashboard-home", "Dashboard", "ri-dashboard-3-line", "/", [
        "/",
        "/dashboard/module",
      ]),
      tab(
        "reporting-dashboard",
        "Report Dashboard",
        "ri-bar-chart-box-line",
        "/reporting/dashboard",
      ),
    ],
    routes: [
      { path: "/", action: "view", title: "Dashboard" },
      { path: "/dashboard/module", action: "view", title: "Module Dashboard" },
      {
        path: "/reporting/dashboard",
        action: "view",
        title: "Reporting Dashboard",
      },
    ],
  },
  {
    key: "enquiry_management",
    title: "Enquiry Management",
    label: "Enquiries",
    subtitle: "Enquiries, follow-ups and reports",
    icon: "ri-question-answer-line",
    sidebar: true,
    order: 10,
    defaultPath: "/enquiry/reports",
    paths: ["/enquiry/reports", "/enquiry", "/enquiry/followups"],
    tabs: [
      tab(
        "enquiry-report",
        "Enquiry Reports",
        "ri-bar-chart-line",
        "/enquiry/reports",
      ),
      tab("enquiries", "Enquiries", "ri-bar-chart-line", "/enquiry", [
        "/enquiry",
        "/enquiry/update",
        "/enquiry/add",
        "/enquiry/detail",
      ]),
      tab(
        "enquiry-followups",
        "Follow-ups",
        "ri-user-add-line",
        "/enquiry/followups",
      ),
      tab(
        "enquiry-form",
        "Enquiry Form",
        "ri-layout-grid-line",
        "/enquiry/form/print",
      ),
      tab(
        "enquiry-demo-form",
        "Demo Form",
        "ri-codepen-line",
        "/enquiry/demo/form/print",
      ),
    ],
    routes: [
      { path: "/enquiry/reports", action: "report" },
      { path: "/enquiry", action: "view" },
      { path: "/enquiry/add", action: "create" },
      { path: "/enquiry/update", action: "update" },
      { path: "/enquiry/detail", action: "view" },
      { path: "/enquiry/followups", action: "view" },
      { path: "/enquiry/form/print", action: "print" },
      { path: "/enquiry/demo/form/print", action: "print" },
      {
        path: "/enquiry/followups/view",
        action: "view",
        title: "Follow Ups",
      },
      {
        path: "/enquiry/form/view",
        action: "view",
        title: "Enquiry Form",
      },
      {
        path: "/enquiry/demo/form/view",
        action: "view",
        title: "Demo Form",
      },
    ],
  },
  {
    key: "student_management",
    title: "Student Management",
    label: "Students",
    subtitle: "Student records, admission and bulk tools",
    icon: "ri-graduation-cap-line",
    sidebar: true,
    order: 20,
    defaultPath: "/students/report",
    paths: [
      "/students/report",
      "/students",
      "/duplicate/students",
      // Profile tabs are rendered inside StudentDetailsV5 (not main navigation).
      "/student/overview",
      "/student/details",
      "/student/batch/and/fees",
      "/student/fee/ransactions",
      "/student/attendance",
      "/student/mark/report",
      "/student/hostel",
      "/student/library",
      "/student/transport",
      "/student/admission/form",
      "/student/demo/form",
      "/student/deleted/batches/Fees",
      "/students/rte",
    ],
    tabs: [
      tab(
        "student-reports",
        "Reports",
        "ri-bar-chart-line",
        "/students/report",
      ),
      tab("students", "Students", "ri-group-line", "/students", [
        "/students",
        "/student/update",
        "/student/add",
        "/student/details",
        "/student/overview",
        "/student/batch/and/fees",
        "/student/fee/ransactions",
        "/student/attendance",
        "/student/mark/report",
        "/student/hostel",
        "/student/library",
        "/student/transport",
        "/student/admission/form",
        "/student/demo/form",
        "/student/deleted/batches/Fees",
      ]),
      tab(
        "student-duplicates",
        "Duplicates",
        "ri-file-copy-line",
        "/duplicate/students",
      ),
      tab(
        "student-login",
        "App Login",
        "ri-smartphone-line",
        "/student/app/login/status",
      ),
      tab(
        "student-admission-form",
        "Admission",
        "ri-file-text-line",
        "/student/admission/form/print",
      ),
      tab(
        "student-demo-form",
        "Demo Form",
        "ri-presentation-line",
        "/student/demo/form/print",
      ),
      tab(
        "student-bulk",
        "Bulk Tools",
        "ri-tools-line",
        "/students/import/v1",
        bulkTools.map((tool) => tool.path),
        { subItems: bulkTools },
      ),
      tab(
        "student-reviews",
        "Student Reviews",
        "ri-star-smile-line",
        "/student-reviews",
      ),
    ],
    routes: [
      { path: "/students/report", action: "report" },
      { path: "/students", action: "view" },
      { path: "/student/add", action: "create" },
      { path: "/student/update", action: "update" },
      {
        path: "/student/details",
        action: "view",
        title: "Student Basic & Academic Details",
      },
      // All StudentDetailsV5 profile tabs are individually grantable, except
      // Basic and Academic which intentionally share /student/details.
      {
        path: "/student/overview",
        action: "view",
        title: "Student Profile Overview",
      },
      {
        path: "/student/batch/and/fees",
        action: "view",
        title: "Student Batch & Fees",
      },
      {
        path: "/student/fee/ransactions",
        action: "view",
        title: "Student Fee Transactions",
      },
      {
        path: "/student/attendance",
        action: "view",
        title: "Student Profile Attendance",
      },
      {
        path: "/student/mark/report",
        action: "report",
        title: "Student Mark Report",
      },
      {
        path: "/student/hostel",
        action: "view",
        title: "Student Profile Hostel",
      },
      {
        path: "/student/library",
        action: "view",
        title: "Student Profile Library",
      },
      {
        path: "/student/transport",
        action: "view",
        title: "Student Profile Transport",
      },
      {
        path: "/student/admission/form",
        action: "print",
        title: "Student Profile Admission Form",
      },
      {
        path: "/student/demo/form",
        action: "print",
        title: "Student Profile Demo Form",
      },
      {
        path: "/student/deleted/batches/Fees",
        action: "view",
        title: "Student Deleted Batches & Fees",
      },
      { path: "/duplicate/students", action: "view" },
      { path: "/student/app/login/status", action: "view" },
      { path: "/student/admission/form/print", action: "print" },
      { path: "/student/demo/form/print", action: "print" },
      { path: "/student-reviews", action: "view", title: "Student Reviews" },
      {
        path: "/students/rte",
        action: "view",
        title: "RTE Students",
      },
      { path: "/students/export", action: "export", title: "Export Students" },
      { path: "/students/print", action: "print", title: "Print Students" },
      {
        path: "/student/fees/deposit",
        action: "create",
        title: "Student Fee Deposit",
      },
      {
        path: "/student/batch/enroll",
        action: "create",
        title: "Student Batch Enrollment",
      },
      {
        path: "/student/bus/enroll",
        action: "create",
        title: "Student Bus Enrollment",
      },
      {
        path: "/hostel/student/add",
        action: "create",
        title: "Add Student to Hostel",
      },
      {
        path: "/student/password/update",
        action: "update",
        title: "Student Password Update",
      },
      {
        path: "/student/documents/add",
        action: "create",
        title: "Add Student Documents",
      },
      ...bulkTools.map((tool) => ({
        path: tool.path,
        action: "bulk",
        title: tool.title,
      })),
    ],
  },
  {
    key: "attendance_management",
    title: "Attendance Management",
    label: "Attendance",
    subtitle: "Student attendance, follow-ups and reporting",
    icon: "ri-calendar-check-line",
    sidebar: true,
    order: 30,
    defaultPath: "/student/attendance/dashboard/v2",
    paths: [
      "/student/attendance/dashboard/v2",
      "/attendance/mark",
      "/attendance/reports/day-wise",
      "/attendance/reports/monthly",
      "/attendance/reports/annual",
    ],
    tabs: [
      tab(
        "attendance-overview",
        "Overview",
        "ri-dashboard-3-line",
        "/student/attendance/dashboard/v2",
      ),
      tab(
        "attendance-mark",
        "Mark Attendance",
        "ri-user-follow-line",
        "/attendance/mark",
      ),
      tab(
        "attendance-day-wise",
        "Day Wise",
        "ri-calendar-check-line",
        "/attendance/reports/day-wise",
      ),
      tab(
        "attendance-monthly",
        "Monthly",
        "ri-calendar-2-line",
        "/attendance/reports/monthly",
      ),
      tab(
        "attendance-annual",
        "Annual",
        "ri-calendar-todo-line",
        "/attendance/reports/annual",
      ),
      tab(
        "attendance-low",
        "Low Attendance",
        "ri-user-unfollow-line",
        "/attendance/reports/low-attendance",
        ["/attendance/reports/low-attendance"],
        { important: true },
      ),
      tab(
        "attendance-message-delivery",
        "Message Delivery",
        "ri-message-3-line",
        "/attendance/reports/message-delivery",
      ),
      tab(
        "attendance-admission-month",
        "Admission Month Wise",
        "ri-user-search-line",
        "/attendance/reports/admission-month-wise",
      ),
      tab(
        "attendance-followups",
        "Follow-ups",
        "ri-phone-line",
        "/student/attendance/followups",
      ),
    ],
    routes: [
      {
        path: "/student/attendance/dashboard/v2",
        action: "report",
        title: "Attendance Overview",
      },
      { path: "/attendance/mark", action: "create", title: "Mark Attendance" },
      {
        path: "/attendance/reports/day-wise",
        action: "report",
        title: "Day Wise",
      },
      {
        path: "/attendance/reports/monthly",
        action: "report",
        title: "Monthly",
      },
      { path: "/attendance/reports/annual", action: "report", title: "Annual" },
      {
        path: "/attendance/reports/low-attendance",
        action: "report",
        title: "Low Attendance",
      },
      {
        path: "/attendance/reports/message-delivery",
        action: "report",
        title: "Message Delivery",
      },
      {
        path: "/attendance/reports/admission-month-wise",
        action: "report",
        title: "Admission Month Wise",
      },
      {
        path: "/student/attendance/followups",
        action: "view",
        title: "Attendance Follow-ups",
      },
      // Other existing attendance pages are tracked here for active sidebar detection.
      {
        path: "/student/attendance/dashboard",
        action: "report",
        title: "Classic Attendance Dashboard",
      },
      {
        path: "/student/attendance/list",
        action: "view",
        title: "Attendance List",
      },
      {
        path: "/student/attendance/messages/list",
        action: "view",
        title: "Attendance Messages",
      },
      {
        path: "/student/attendance/manual",
        action: "create",
        title: "Legacy Manual Attendance",
      },
      {
        path: "/student/attendance/list/monthly",
        action: "report",
        title: "Legacy Monthly Register",
      },
      {
        path: "/student/attendance/list/annual",
        action: "report",
        title: "Legacy Annual Register",
      },
      {
        path: "/student-attendance/student/:studentId/report",
        action: "report",
        title: "Student Attendance Detail",
      },
    ],
  },
  {
    key: "examination_management",
    title: "Examination Management",
    label: "Examination",
    subtitle: "Examinations, tests, grading and results",
    icon: "ri-file-list-3-line",
    sidebar: true,
    order: 40,
    defaultPath: "/tests",
    paths: [
      "/tests",
      "/tests/add_test",
      "/tests/view",
      "/test/grading",
      "/test/marks/lock",
      "/test/marks/unlock",
      "/tests/report/obtained/message/send",
    ],
    tabs: [
      tab("seriesList", "Series List", "ri-list-check-2", "/tests", [
        "/tests",
        "/tests/add",
        "/tests/edit",
      ]),
      tab(
        "createExam",
        "Create Exam/Test",
        "ri-file-add-line",
        "/tests/add_test",
      ),
      tab("examList", "Exam/Test List", "ri-file-list-3-line", "/tests/view", [
        "/tests/view",
        "/tests/report",
        "/tests/update_test",
        "/tests/excel/marks/upload",
      ]),
      tab("test-grading", "Test Grading", "ri-medal-line", "/test/grading"),
      tab("result", "Test Wise Result", "ri-line-chart-line", "/progressCard"),
      tab(
        "annualReport",
        "Annual Report",
        "ri-bar-chart-box-line",
        "/annual/report/table",
      ),
      tab(
        "admitCard",
        "Admit Card",
        "ri-id-card-line",
        "/certificates/admitcard",
      ),
      tab("marksheet", "Marksheet", "ri-file-text-line", "/student/marksheet"),
    ],
    routes: [
      { path: "/tests", action: "view", title: "Series List" },
      { path: "/tests/add", action: "create", title: "Add Test Series" },
      { path: "/tests/edit", action: "update", title: "Edit Test Series" },
      { path: "/tests/add_test", action: "create", title: "Create Exam/Test" },
      { path: "/tests/view", action: "view", title: "Exam/Test List" },
      { path: "/tests/report", action: "report", title: "Test Report" },
      { path: "/tests/update_test", action: "update", title: "Update Test" },
      {
        path: "/tests/excel/marks/upload",
        action: "update",
        title: "Upload Test Marks",
      },
      { path: "/test/grading", action: "view", title: "Test Grading" },
      { path: "/progressCard", action: "report", title: "Test Wise Result" },
      {
        path: "/annual/report/table",
        action: "report",
        title: "Annual Report",
      },
      { path: "/certificates/admitcard", action: "print", title: "Admit Card" },
      { path: "/student/marksheet", action: "print", title: "Marksheet" },
      {
        path: "/test/marks/lock",
        action: "update",
        title: "Lock Marks",
      },
      {
        path: "/test/marks/unlock",
        action: "update",
        title: "Unlock Marks",
      },
      {
        path: "/tests/report/obtained/message/send",
        action: "update",
        title: "Send Test Result Message",
      },
      {
        path: "/tests/report/print",
        action: "print",
        title: "Print",
      },
      {
        path: "/tests/obtained/marks/export",
        action: "report",
        title: "Export ",
      },
    ],
  },
  {
    key: "timetable_management",
    title: "Timetable Management",
    label: "Timetable",
    subtitle: "Schedules, timetable setup and day-wise view",
    icon: "ri-calendar-schedule-line",
    sidebar: true,
    order: 50,
    defaultPath: "/schedule",
    paths: [
      "/schedule",
      "/timetable/manage",
      "/timetable",
      "/timetable/day/wise",
    ],
    tabs: [
      tab("schedule", "Schedule", "ri-calendar-2-line", "/schedule", [
        "/schedule",
        "/schedule/edit",
        "/schedule/add",
        "/schedule/delete",
      ]),
      tab(
        "timetableManage",
        "Timetable Manage",
        "ri-settings-3-line",
        "/timetable/manage",
      ),
      tab(
        "timeTable",
        "Time Table",
        "ri-calendar-schedule-line",
        "/timetable",
        ["/timetable", "/timetable/print"],
      ),
      tab("dayWise", "Day Wise", "ri-calendar-2-line", "/timetable/day/wise"),
    ],
    routes: [
      { path: "/schedule", action: "view", title: "Schedule" },
      { path: "/schedule/edit", action: "update", title: "Edit Schedule" },
      { path: "/schedule/add", action: "create", title: "Add Schedule" },
      { path: "/schedule/delete", action: "delete", title: "Delete Schedule" },
      {
        path: "/timetable/manage",
        action: "update",
        title: "Timetable Manage",
      },
      { path: "/timetable", action: "view", title: "Time Table" },
      { path: "/timetable/print", action: "print", title: "Print Timetable" },
      { path: "/timetable/day/wise", action: "view", title: "Day Wise" },
    ],
  },
  {
    key: "fees_report_management",
    title: "Fees Report Management",
    label: "Fees Report",
    subtitle: "Fee collections, due reports and transactions",
    icon: "ri-money-rupee-circle-line",
    sidebar: true,
    order: 60,
    defaultPath: "/fees/report",
    paths: ["/fees/report", "/fees/transactions", "/student/due/fee/type-wise"],
    tabs: [
      tab("feesReport", "Fees Report", "ri-file-chart-2-line", "/fees/report"),
      tab(
        "feesTxn",
        "Fees Transactions",
        "ri-exchange-dollar-line",
        "/fees/transactions",
      ),
      tab(
        "dueFeesTypeWise",
        "Due Fees Type Wise",
        "ri-pie-chart-2-line",
        "/student/due/fee/type-wise",
      ),
      tab(
        "batchFeeReport",
        "Batch Fee Report",
        "ri-graduation-cap-line",
        "/batch/fee/report",
      ),
      tab(
        "discountFeeReport",
        "Discount Fees Report",
        "ri-price-tag-3-line",
        "/fee/discount/report",
      ),
      tab(
        "pendingFeeTxn",
        "Pending Fee Transactions",
        "ri-time-line",
        "/all/fees/transactions",
        ["/all/fees/transactions", "/student/fees/transaction/history"],
      ),
      tab(
        "manualFeeDeposit",
        "Manual Fee Deposit",
        "ri-hand-coin-line",
        "/student/manual/fee/deposit",
      ),
      tab(
        "allFeeTxn",
        "All Fee Transactions",
        "ri-money-rupee-circle-line",
        "/fees/transactions/v2",
      ),
      tab(
        "familyFeeDeposit",
        "Family Fee Deposit",
        "ri-group-line",
        "/family/fees/deposit",
      ),
    ],
    routes: [
      { path: "/fees/report", action: "report", title: "Fees Report" },
      {
        path: "/fees/transactions",
        action: "report",
        title: "Fees Transactions",
      },
      {
        path: "/student/due/fee/type-wise",
        action: "report",
        title: "Due Fees Type Wise",
      },
      {
        path: "/batch/fee/report",
        action: "report",
        title: "Batch Fee Report",
      },
      {
        path: "/fee/discount/report",
        action: "report",
        title: "Discount Fees Report",
      },
      {
        path: "/all/fees/transactions",
        action: "report",
        title: "Pending Fee Transactions",
      },
      {
        path: "/student/fees/transaction/history",
        action: "report",
        title: "Transaction History",
      },
      {
        path: "/student/manual/fee/deposit",
        action: "create",
        title: "Manual Fee Deposit",
      },
      {
        path: "/fees/transactions/v2",
        action: "report",
        title: "All Fee Transactions",
      },
      {
        path: "/family/fees/deposit",
        action: "create",
        title: "Family Fee Deposit",
      },
    ],
  },
  {
    key: "expenses_management",
    title: "Expenses Management",
    label: "Expenses",
    subtitle: "Manage school expenses and categories",
    icon: "ri-wallet-3-line",
    sidebar: true,
    order: 70,
    defaultPath: "/expenses",

    paths: [
      // Expenses
      "/expenses",
      "/expenses/add",
      "/expenses/view",
      "/expenses/update",
      "/expense/export",
      "/expense/print",

      // Expense Category
      "/expense/category",
      "/expense/category/add",
      "/expense/category/edit",
    ],

    tabs: [
      tab(
        "expenses-list",
        "Expenses",
        "ri-money-dollar-circle-line",
        "/expenses",
      ),
      tab(
        "expense-categories",
        "Expense Category",
        "ri-folder-2-line",
        "/expense/category",
      ),
    ],

    routes: [
      // Expenses permissions
      {
        path: "/expenses",
        action: "view",
        title: "Expenses",
      },
      {
        path: "/expenses/add",
        action: "create",
        title: "Add Expense",
      },
      {
        path: "/expenses/view",
        action: "view",
        title: "View Expense",
      },
      {
        path: "/expenses/update",
        action: "update",
        title: "Edit Expense",
      },
      {
        path: "/expense/export",
        action: "export",
        title: "Export Expenses",
      },
      {
        path: "/expense/print",
        action: "print",
        title: "Print Expenses",
      },
      {
        path: "/expense/category",
        action: "view",
        title: "Expense Category",
      },
      {
        path: "/expense/category/add",
        action: "create",
        title: "Add Expense Category",
      },
      {
        path: "/expense/category/edit",
        action: "update",
        title: "Edit Expense Category",
      },
    ],
  },

  {
    key: "banks_management",
    title: "Banks Management",
    label: "Banks",
    subtitle: "Bank accounts, transactions and fund transfers",
    icon: "ri-bank-line",
    sidebar: true,
    order: 80,
    defaultPath: "/settings/bank",
    paths: [
      "/account/bank/add",
      "/settings/bank",
      "/settings/bank/fund/transfer",
    ],
    tabs: [
      tab(
        "bank-account-add",
        "Bank Account Add",
        "ri-bank-line",
        "/account/bank/add",
      ),
      tab(
        "bank-account-list",
        "Bank Account List",
        "ri-list-check",
        "/settings/bank",
        ["/settings/bank", "/settings/bank/transactions/:id"],
      ),
      tab(
        "bank-fund-transfer",
        "Fund Transfer Transaction",
        "ri-history-line",
        "/settings/bank/fund/transfer",
      ),
    ],
    routes: [
      {
        path: "/account/bank/add",
        action: "create",
        title: "Bank Account Add",
      },
      { path: "/settings/bank", action: "view", title: "Bank Account List" },
      {
        path: "/settings/bank/transactions/:id",
        action: "view",
        title: "Bank Transactions",
      },
      {
        path: "/settings/bank/fund/transfer",
        action: "create",
        title: "Fund Transfer Transaction",
      },
    ],
  },
  {
    key: "staff_management",
    title: "Staff Management",
    label: "Staffs",
    subtitle: "Staff, schedules, payroll, reports and staff access",
    icon: "ri-team-line",
    sidebar: true,
    order: 90,
    defaultPath: "/staffs",
    paths: [
      "/staffs",
      "/teacher/working/schedule",
      "/staff/salary/pay",
      "/staff/report/dashboard",
      "/staff/access",
    ],
    tabs: [
      tab("staff-list", "Staff List", "ri-team-line", "/staffs", [
        "/staffs",
        "/teachers/view/:id",
        "/staff/add",
        "/staff/update",
      ]),
      tab(
        "staff-working-schedule",
        "Working Schedule",
        "ri-calendar-check-line",
        "/teacher/working/schedule",
        ["/teacher/working/schedule", "/staff/schedule/create"],
      ),
      tab(
        "staff-pay-salary",
        "Pay Salary",
        "ri-money-rupee-circle-line",
        "/staff/salary/pay",
      ),
      tab(
        "staff-report-dashboard",
        "Report Dashboard",
        "ri-file-chart-line",
        "/staff/report/dashboard",
      ),
      tab(
        "staff-access-management",
        "Staff Access",
        "ri-shield-user-line",
        "/staff/access",
        ["/staff/access"],
      ),
    ],
    routes: [
      { path: "/staffs", action: "view", title: "Staff List" },
      { path: "/teachers/view/:id", action: "view", title: "Staff Details" },
      { path: "/staff/add", action: "create", title: "Add Staff" },
      { path: "/staff/update", action: "update", title: "Update Staff" },
      {
        path: "/teacher/working/schedule",
        action: "view",
        title: "Working Schedule",
      },
      {
        path: "/staff/schedule/create",
        action: "create",
        title: "Create Staff Schedule",
      },
      { path: "/staff/salary/pay", action: "create", title: "Pay Salary" },
      {
        path: "/staff/report/dashboard",
        action: "report",
        title: "Staff Report Dashboard",
      },
      { path: "/staff/access", action: "update", title: "Manage Staff Access" },
      {
        path: "/staff/salary/details",
        action: "view",
        title: "Staff Salary Details",
      },
      {
        path: "/staff/salary/report",
        action: "report",
        title: "Staff Salary Report",
      },
      {
        path: "/staff/attendance/report",
        action: "report",
        title: "Staff Attendance Report",
      },
      {
        path: "/staff/work/report",
        action: "report",
        title: "Staff Work Report",
      },
      {
        path: "/staff/document/add",
        action: "create",
        title: "Add Staff Document",
      },
    ],
  },
  {
    key: "staff_attendance_management",
    title: "Staff Attendance Management",
    label: "Staff Attendance",
    subtitle: "Staff attendance overview, daily and monthly records",
    icon: "ri-calendar-check-line",
    sidebar: true,
    order: 91,
    defaultPath: "/staff/attendance/dashboard",
    paths: [
      "/staff/attendance/dashboard",
      "/staff/attendance/list",
      "/staff/attendance/list/monthly",
    ],
    tabs: [
      tab(
        "staff-attendance-dashboard",
        "Dashboard",
        "ri-dashboard-3-line",
        "/staff/attendance/dashboard",
      ),
      tab(
        "staff-attendance-list",
        "Attendance List",
        "ri-file-list-3-line",
        "/staff/attendance/list",
      ),
      tab(
        "staff-attendance-monthly",
        "Monthly Attendance",
        "ri-calendar-2-line",
        "/staff/attendance/list/monthly",
      ),
    ],
    routes: [
      {
        path: "/staff/attendance/dashboard",
        action: "report",
        title: "Staff Attendance Dashboard",
      },
      {
        path: "/staff/attendance/list",
        action: "view",
        title: "Staff Attendance List",
      },
      {
        path: "/staff/attendance/list/monthly",
        action: "report",
        title: "Staff Monthly Attendance",
      },
    ],
  },
  {
    key: "attendance_devices_management",
    title: "Attendance Machines & Logs Management",
    label: "Attendance Machines",
    subtitle: "Manage biometric devices and attendance logs",
    icon: "ri-fingerprint-line",
    sidebar: true,
    order: 92,
    defaultPath: "/essl/devices/list",
    paths: [
      "/essl/devices/list",
      "/essl/devices/logs",
      "/essl/devices/students",
    ],
    tabs: [
      tab(
        "essl-device-list",
        "Machines",
        "ri-device-line",
        "/essl/devices/list",
      ),
      tab(
        "essl-device-logs",
        "Attendance Logs",
        "ri-file-list-3-line",
        "/essl/devices/logs",
      ),
      // tab(
      //   "essl-device-users",
      //   "Registered Users",
      //   "ri-group-line",
      //   "/essl/devices/students",
      // ),
    ],
    routes: [
      {
        path: "/essl/devices/list",
        action: "view",
        title: "Biometric Devices",
      },
      { path: "/essl/devices/logs", action: "view", title: "Biometric Logs" },
      {
        path: "/essl/devices/students",
        action: "view",
        title: "Registered Biometric Users",
      },
      {
        path: "/essl/device/add",
        action: "create",
        title: "Add Biometric Device",
      },
      {
        path: "/essl/devices/update",
        action: "update",
        title: "Update Biometric Device",
      },
    ],
  },
  {
    key: "holidays_leave_management",
    title: "Holidays & Leave Management",
    label: "Holidays & Leaves",
    subtitle: "School holidays and leave requests",
    icon: "ri-calendar-event-line",
    sidebar: true,
    order: 93,
    defaultPath: "/holidays",
    paths: ["/holidays", "/leave/request", "/holidays/add", "/holidays/update"],
    tabs: [
      tab("holidays-list", "Holidays", "ri-calendar-event-line", "/holidays", [
        "/holidays",
        "/holidays/add",
        "/holidays/update",
      ]),
      tab(
        "leave-requests",
        "Leave Requests",
        "ri-file-list-3-line",
        "/leave/request",
      ),
    ],
    routes: [
      { path: "/holidays", action: "view", title: "Holidays" },
      { path: "/holidays/add", action: "create", title: "Add Holiday" },
      { path: "/holidays/update", action: "update", title: "Update Holiday" },
      { path: "/leave/request", action: "view", title: "Leave Requests" },
    ],
  },
  {
    key: "documents_certificates_management",
    title: "Documents & Certificates Management",
    label: "Documents & Certificates",
    subtitle: "ID cards, profiles and student certificates",
    icon: "ri-folder-user-line",
    sidebar: true,
    order: 100,
    defaultPath: "/student/id-card",
    paths: [
      "/student/id-card",
      "/idcard_staff",
      "/idcard/student",
      "/student/transfer-certificate",
    ],
    tabs: [
      tab(
        "student-id-card",
        "Student ID Card",
        "ri-id-card-line",
        "/student/id-card",
      ),
      tab(
        "staff-id-card",
        "Staff ID Card",
        "ri-pass-valid-line",
        "/idcard_staff",
      ),
      tab(
        "profile-update",
        "Profile Update",
        "ri-account-pin-box-line",
        "/idcard/student",
      ),
      tab(
        "transfer-certificate",
        "Transfer Certificate",
        "ri-file-paper-2-line",
        "/student/transfer-certificate",
      ),
      tab(
        "character-certificate",
        "Character Certificate",
        "ri-file-list-3-line",
        "/student/character-certificate",
      ),
      tab(
        "study-certificate",
        "Study Certificate",
        "ri-file-text-line",
        "/student/study-certificate",
      ),
    ],
    routes: [
      { path: "/student/id-card", action: "print", title: "Student ID Card" },
      { path: "/idcard_staff", action: "print", title: "Staff ID Card" },
      { path: "/idcard/student", action: "update", title: "Profile Update" },
      {
        path: "/student/transfer-certificate",
        action: "print",
        title: "Transfer Certificate",
      },
      {
        path: "/student/character-certificate",
        action: "print",
        title: "Character Certificate",
      },
      {
        path: "/student/study-certificate",
        action: "print",
        title: "Study Certificate",
      },
    ],
  },
  {
    key: "hostel_management",
    title: "Hostel Management",
    label: "Hostel",
    subtitle: "Hostel students, rooms, fees, attendance and reports",
    icon: "ri-hotel-bed-line",
    sidebar: true,
    order: 110,
    defaultPath: "/hostel/list",
    paths: [
      "/hostel/list",
      "/hostel",
      "/hostel/students",
      "/hostel/rooms",
      "/hostel/reports",
    ],
    tabs: [
      tab("hostel-list", "Hostel List", "ri-hotel-bed-line", "/hostel/list"),
      tab("hostel-dashboard", "Dashboard", "ri-dashboard-3-line", "/hostel"),
      tab("hostel-students", "Students", "ri-group-line", "/hostel/students", [
        "/hostel/students",
        "/hostel/student/add",
        "/hostel/student/transfer",
        "/hostel/student/id-card",
      ]),
      tab("hostel-rooms", "Rooms", "ri-home-4-line", "/hostel/rooms"),
      tab("hostel-hostels", "Hostels", "ri-building-2-line", "/hostel/hostels"),
      tab(
        "hostel-deposit",
        "Fee Deposit",
        "ri-hand-coin-line",
        "/hostel/fees/deposit",
        ["/hostel/fees/deposit", "/hostel/fee_receipt"],
      ),
      tab(
        "hostel-fee-pay",
        "Fee Payment",
        "ri-bank-card-line",
        "/hostel/fees/pay",
      ),
      tab(
        "hostel-attendance",
        "Mark Attendance",
        "ri-calendar-check-line",
        "/hostel/student/attendance",
      ),
      tab(
        "hostel-attendance-list",
        "Attendance List",
        "ri-file-list-3-line",
        "/hostel/student/attendance/list",
      ),
      tab(
        "hostel-attendance-monthly",
        "Monthly Attendance",
        "ri-calendar-2-line",
        "/hostel/student/attendance/list/monthly",
      ),
      tab(
        "hostel-expenses",
        "Expenses",
        "ri-wallet-3-line",
        "/hostel/expenses",
      ),
      tab(
        "hostel-expense-category",
        "Expense Category",
        "ri-folder-2-line",
        "/hostel/expense/category",
      ),
      tab(
        "hostel-reports",
        "Reports",
        "ri-bar-chart-box-line",
        "/hostel/reports",
      ),
      tab(
        "hostel-profile",
        "Hostel Profile",
        "ri-user-settings-line",
        "/hostel/profile",
      ),
    ],
    routes: [
      { path: "/hostel/list", action: "view", title: "Hostel List" },
      { path: "/hostel", action: "view", title: "Hostel Dashboard" },
      { path: "/hostel/students", action: "view", title: "Hostel Students" },
      {
        path: "/hostel/student/add",
        action: "create",
        title: "Add Hostel Student",
      },
      {
        path: "/hostel/fees/deposit",
        action: "create",
        title: "Hostel Fee Deposit",
      },
      {
        path: "/hostel/fees/pay",
        action: "create",
        title: "Hostel Fee Payment",
      },
      {
        path: "/hostel/fee_receipt",
        action: "print",
        title: "Hostel Fee Receipt",
      },
      { path: "/hostel/rooms", action: "view", title: "Hostel Rooms" },
      { path: "/hostel/hostels", action: "view", title: "Hostels" },
      { path: "/hostel/reports", action: "report", title: "Hostel Reports" },
      {
        path: "/hostel/expense/category",
        action: "view",
        title: "Hostel Expense Category",
      },
      { path: "/hostel/expenses", action: "view", title: "Hostel Expenses" },
      {
        path: "/hostel/student/id-card",
        action: "print",
        title: "Hostel Student ID Card",
      },
      {
        path: "/hostel/student/transfer",
        action: "update",
        title: "Hostel Student Transfer",
      },
      {
        path: "/hostel/student/attendance",
        action: "create",
        title: "Hostel Student Attendance",
      },
      {
        path: "/hostel/student/attendance/list",
        action: "view",
        title: "Hostel Attendance List",
      },
      {
        path: "/hostel/student/attendance/list/monthly",
        action: "report",
        title: "Hostel Monthly Attendance",
      },
      { path: "/hostel/profile", action: "view", title: "Hostel Profile" },
    ],
  },
  {
    key: "mess_management",
    title: "Mess Management",
    label: "Mess",
    subtitle: "Mess enrollment and attendance",
    icon: "ri-restaurant-2-line",
    sidebar: true,
    order: 120,
    defaultPath: "/mess/list",
    paths: [
      "/mess/list",
      "/mess/enroll/list",
      "/mess/attendance/mark",
      "/mess/attendance/list",
      "/mess/attendance/log",
      "/mess/attendance/summary",
    ],
    tabs: [
      tab("mess", "Mess", "ri-hotel-line", "/mess/list"),
      tab("mess-enroll", "Enrolls", "ri-settings-2-line", "/mess/enroll/list", [
        "/mess/enroll/list",
        "/mess/enroll",
      ]),
      tab(
        "mess-mark",
        "Mark Attendance",
        "ri-checkbox-circle-line",
        "/mess/attendance/mark",
      ),
      tab(
        "mess-attendance",
        "Attendance",
        "ri-account-circle-line",
        "/mess/attendance/list",
      ),
      tab("mess-log", "Log", "ri-history-line", "/mess/attendance/log"),
      tab(
        "mess-summary",
        "Monthly Summary",
        "ri-file-chart-line",
        "/mess/attendance/summary",
      ),
    ],
    routes: [
      { path: "/mess/list", action: "view", title: "Mess" },
      { path: "/mess/enroll/list", action: "view", title: "Enrolls" },
      { path: "/mess/enroll", action: "create", title: "Enrolls" },
      {
        path: "/mess/attendance/mark",
        action: "create",
        title: "Mark Attendance",
      },
      { path: "/mess/attendance/list", action: "view", title: "Attendance" },
      { path: "/mess/attendance/log", action: "view", title: "Log" },
      {
        path: "/mess/attendance/summary",
        action: "report",
        title: "Monthly Summary",
      },
      { path: "/mess/guest/card", action: "print", title: "Guest Card" },
    ],
  },
  {
    key: "transport_management",
    title: "Transport Management",
    label: "Transport",
    subtitle: "Transport operations and bus tracking",
    icon: "ri-bus-line",
    sidebar: true,
    order: 130,
    defaultPath: "/transport/report",
    paths: [
      "/transport/report",
      "/bus/list",
      "/roots/stops/list",
      "/bus/student/list",
      "/bus/live",
      "/bus/pass",
    ],
    tabs: [
      tab(
        "transport-report",
        "Transport Report",
        "ri-file-chart-line",
        "/transport/report",
      ),
      tab("transport-bus", "Bus List", "ri-bus-line", "/bus/list"),
      tab(
        "transport-stops",
        "Roots Stops List",
        "ri-map-pin-line",
        "/roots/stops/list",
        ["/roots/stops/list", "/bus/stop/import"],
      ),
      tab(
        "transport-students",
        "Bus Student List",
        "ri-group-line",
        "/bus/student/list",
        [
          "/bus/student/list",
          "/bus/students/import",
          "/bus/student/import",
          "/student/bus/enroll",
          "/student/bus/enrollment/edit",
        ],
      ),
      tab(
        "transport-live",
        "Bus Live Tracking",
        "ri-map-pin-2-line",
        "/bus/live",
        ["/bus/live", "/bus/live/tracking"],
      ),
      tab("transport-pass", "Bus Pass", "ri-bank-card-line", "/bus/pass"),
    ],
    routes: [
      {
        path: "/transport/report",
        action: "report",
        title: "Transport Report",
      },
      { path: "/bus/list", action: "view", title: "Bus List" },
      { path: "/roots/stops/list", action: "view", title: "Roots Stops List" },
      { path: "/bus/stop/import", action: "view", title: "Roots Stops List" },
      { path: "/bus/student/list", action: "view", title: "Bus Student List" },
      {
        path: "/bus/students/import",
        action: "view",
        title: "Bus Student List",
      },
      {
        path: "/bus/student/import",
        action: "view",
        title: "Bus Student List",
      },
      {
        path: "/student/bus/enroll",
        action: "create",
        title: "Bus Student List",
      },
      {
        path: "/student/bus/enrollment/edit",
        action: "update",
        title: "Bus Student List",
      },
      { path: "/bus/live", action: "view", title: "Bus Live Tracking" },
      {
        path: "/bus/live/tracking",
        action: "view",
        title: "Bus Live Tracking",
      },
      { path: "/bus/pass", action: "print", title: "Bus Pass" },
    ],
  },
  {
    key: "documents_management",
    title: "Documents Management",
    label: "Documents",
    subtitle: "Document types, student collections and distribution",
    icon: "ri-folder-3-line",
    sidebar: true,
    order: 140,
    defaultPath: "/documents/type",
    paths: ["/documents/type", "/student/documents"],
    tabs: [
      tab("doc-type", "Documents Type", "ri-bar-chart-line", "/documents/type"),
      tab(
        "doc-student",
        "Documents",
        "ri-file-text-line",
        "/student/documents",
        ["/student/documents", "/student/documents/add", "/collection/update"],
      ),
    ],
    routes: [
      { path: "/documents/type", action: "view", title: "Documents Type" },
      { path: "/student/documents", action: "view", title: "Documents" },
      { path: "/student/documents/add", action: "create", title: "Documents" },
      { path: "/collection/update", action: "update", title: "Documents" },
    ],
  },
  {
    key: "distribution_kits_management",
    title: "Distribution Kits Management",
    label: "Distribution Kits",
    subtitle: "Distribution kits, reports and payments",
    icon: "ri-archive-stack-line",
    sidebar: true,
    order: 150,
    defaultPath: "/distribution/kits",
    paths: ["/distribution/kits", "/distribution-kits/report"],
    tabs: [
      tab(
        "kit-distribution",
        "Distribution Kit",
        "ri-archive-stack-line",
        "/distribution/kits",
        ["/distribution/kits", "/distribution/kits/items"],
      ),
      tab(
        "kit-report",
        "Distribution Report",
        "ri-file-chart-line",
        "/distribution-kits/report",
        [
          "/distribution-kits/report",
          "/distribution-kits/payment-receipt",
          "/distribution-kits/payment",
        ],
      ),
    ],
    routes: [
      { path: "/distribution/kits", action: "view", title: "Distribution Kit" },
      {
        path: "/distribution/kits/items",
        action: "view",
        title: "Distribution Kit",
      },
      {
        path: "/distribution-kits/report",
        action: "report",
        title: "Distribution Report",
      },
      {
        path: "/distribution-kits/payment-receipt",
        action: "view",
        title: "Distribution Report",
      },
      {
        path: "/distribution-kits/payment",
        action: "view",
        title: "Distribution Report",
      },
    ],
  },
  {
    key: "meta_whatsapp_management",
    title: "Meta WhatsApp Management",
    label: "Meta WhatsApp",
    subtitle: "Messaging, notifications, templates and wallet",
    icon: "ri-whatsapp-line",
    sidebar: true,
    order: 160,
    defaultPath: "/meta/wa_send_msg",
    paths: [
      "/meta/wa_send_msg",
      "/notification/send",
      "/meta/history",
      "/notification/history",
      "/meta/templates",
      "/message/list",
      "/meta/wallet",
    ],
    tabs: [
      tab(
        "meta-send",
        "Send WhatsApp Notification",
        "ri-send-plane-line",
        "/meta/wa_send_msg",
      ),
      tab(
        "meta-push",
        "Send App Notification",
        "ri-notification-badge-line",
        "/notification/send",
      ),
      tab(
        "meta-history",
        "WhatsApp History",
        "ri-whatsapp-line",
        "/meta/history",
      ),
      tab(
        "meta-notification-history",
        "Notification History",
        "ri-notification-3-line",
        "/notification/history",
      ),
      tab(
        "meta-templates",
        "Meta Templates",
        "ri-file-copy-2-line",
        "/meta/templates",
        ["/meta/templates", "/meta/templates/create"],
      ),
      tab(
        "meta-config",
        "Configuration",
        "ri-settings-3-line",
        "/message/list",
        ["/message/list", "/message/add", "/message/update"],
      ),
      tab("meta-wallet", "Wallet", "ri-wallet-3-line", "/meta/wallet"),
      // Supply your real META_DASHBOARD_URL when available. The navbar should
      // only open this tab externally when externalUrl is configured.
    ],
    routes: [
      {
        path: "/meta/wa_send_msg",
        action: "create",
        title: "Send WhatsApp Notification",
      },
      {
        path: "/notification/send",
        action: "create",
        title: "Send App Notification",
      },
      { path: "/meta/history", action: "view", title: "WhatsApp History" },
      {
        path: "/notification/history",
        action: "view",
        title: "Notification History",
      },
      { path: "/meta/templates", action: "view", title: "Meta Templates" },
      {
        path: "/meta/templates/create",
        action: "create",
        title: "Meta Templates",
      },
      { path: "/message/list", action: "view", title: "Configuration" },
      { path: "/message/add", action: "create", title: "Configuration" },
      { path: "/message/update", action: "update", title: "Configuration" },
      { path: "/meta/wallet", action: "view", title: "Wallet" },
    ],
  },
  {
    key: "academic_portal_management",
    title: "Academic Portal Management",
    label: "Academic Portal",
    subtitle: "Batches, courses, subjects and sessions",
    icon: "ri-school-line",
    sidebar: true,
    order: 170,
    defaultPath: "/batches",
    paths: [
      "/batches",
      "/courses",
      "/subjects",
      "/sessions",
      "/discount/offers/list",
    ],
    tabs: [
      tab("academic-batches", "Batches", "ri-graduation-cap-line", "/batches", [
        "/batches",
        "/batch/add",
        "/batch/update",
        "/batch/detail",
        "/batch/promote",
      ]),
      tab("academic-courses", "Courses", "ri-book-open-line", "/courses", [
        "/courses",
        "/course/add",
        "/course/update",
      ]),
      tab("academic-subjects", "Subjects", "ri-book-3-line", "/subjects"),
      tab("academic-sessions", "Sessions", "ri-calendar-2-line", "/sessions"),
      tab(
        "academic-discounts",
        "Discount Offers",
        "ri-percent-line",
        "/discount/offers/list",
        [
          "/discount/offers/list",
          "/discount/offers/add",
          "/discount/offers/update",
        ],
      ),
    ],
    routes: [
      { path: "/batches", action: "view", title: "Batches" },
      { path: "/batch/add", action: "create", title: "Batches" },
      { path: "/batch/update", action: "update", title: "Batches" },
      { path: "/batch/detail", action: "view", title: "Batches" },
      { path: "/batch/promote", action: "view", title: "Batches" },
      { path: "/courses", action: "view", title: "Courses" },
      { path: "/course/add", action: "create", title: "Courses" },
      { path: "/course/update", action: "update", title: "Courses" },
      { path: "/subjects", action: "view", title: "Subjects" },
      { path: "/sessions", action: "view", title: "Sessions" },
      {
        path: "/discount/offers/list",
        action: "view",
        title: "Discount Offers",
      },
      {
        path: "/discount/offers/add",
        action: "create",
        title: "Discount Offers",
      },
      {
        path: "/discount/offers/update",
        action: "update",
        title: "Discount Offers",
      },
    ],
  },
  {
    key: "academic_activities_management",
    title: "Academic Activities Management",
    label: "Academic Activities",
    subtitle: "Assignments, notices, syllabus and staff work",
    icon: "ri-book-open-line",
    sidebar: true,
    order: 180,
    defaultPath: "/assignment",
    paths: [
      "/assignment",
      "/feedback",
      "/settings/banner",
      "/syllabus",
      "/syllabus/assign",
      "/work/report/list",
      "/notice/board",
    ],
    tabs: [
      tab(
        "activities-assignment",
        "Assignment List",
        "ri-book-open-line",
        "/assignment",
        [
          "/assignment",
          "/assignment/add",
          "/assignments/view",
          "/assignments/student-status",
        ],
      ),
      tab(
        "activities-feedback",
        "FeedBack List",
        "ri-chat-check-line",
        "/feedback",
      ),
      tab("activities-banner", "Banner", "ri-image-line", "/settings/banner"),
      tab("activities-syllabus", "Syllabus", "ri-book-open-line", "/syllabus", [
        "/syllabus",
        "/syllabus/add",
        "/syllabus/update",
      ]),
      tab(
        "activities-tracking",
        "Syllabus Tracking",
        "ri-checkbox-multiple-line",
        "/syllabus/assign",
        ["/syllabus/assign", "/syllabus/assign/add", "/syllabus/assign/update"],
      ),
      tab(
        "activities-staff-work",
        "Staff Work Reports",
        "ri-file-list-3-line",
        "/work/report/list",
      ),
      tab(
        "activities-notice",
        "Notice Board",
        "ri-notification-3-line",
        "/notice/board",
      ),
    ],
    routes: [
      { path: "/assignment", action: "view", title: "Assignment List" },
      { path: "/assignment/add", action: "create", title: "Assignment List" },
      { path: "/assignments/view", action: "view", title: "Assignment List" },
      {
        path: "/assignments/student-status",
        action: "view",
        title: "Assignment List",
      },
      { path: "/feedback", action: "view", title: "FeedBack List" },
      { path: "/settings/banner", action: "view", title: "Banner" },
      { path: "/syllabus", action: "view", title: "Syllabus" },
      { path: "/syllabus/add", action: "create", title: "Syllabus" },
      { path: "/syllabus/update", action: "update", title: "Syllabus" },
      { path: "/syllabus/assign", action: "view", title: "Syllabus Tracking" },
      {
        path: "/syllabus/assign/add",
        action: "create",
        title: "Syllabus Tracking",
      },
      {
        path: "/syllabus/assign/update",
        action: "update",
        title: "Syllabus Tracking",
      },
      {
        path: "/work/report/list",
        action: "view",
        title: "Staff Work Reports",
      },
      { path: "/notice/board", action: "view", title: "Notice Board" },
    ],
  },
  {
    key: "marketing_management",
    title: "Marketing Management",
    label: "Marketing",
    subtitle: "Sources, leads, calls and follow-ups",
    icon: "ri-megaphone-line",
    sidebar: true,
    order: 190,
    defaultPath: "/marketing",
    paths: [
      "/marketing",
      "/marketing/add_school",
      "/marketing/add_student",
      "/marketing/all-students",
      "/AssignLeads",
      "/CounselorCall",
      "/Followup",
    ],
    tabs: [
      tab("marketing-source", "Source List", "ri-list-check-2", "/marketing", [
        "/marketing",
        "/marketing/update_school",
      ]),
      tab(
        "marketing-create",
        "Create Source",
        "ri-edit-box-line",
        "/marketing/add_school",
      ),
      tab(
        "marketing-add-student",
        "Add Student",
        "ri-file-add-line",
        "/marketing/add_student",
      ),
      tab(
        "marketing-students",
        "Student List",
        "ri-eye-line",
        "/marketing/all-students",
        ["/marketing/all-students", "/marketing/update_student"],
      ),
      tab(
        "marketing-assign",
        "Source Assign",
        "ri-graduation-cap-line",
        "/AssignLeads",
      ),
      tab("marketing-call", "Call", "ri-phone-line", "/CounselorCall"),
      tab("marketing-followups", "Follow ups", "ri-id-card-line", "/Followup"),
    ],
    routes: [
      { path: "/marketing", action: "view", title: "Source List" },
      {
        path: "/marketing/update_school",
        action: "update",
        title: "Source List",
      },
      {
        path: "/marketing/add_school",
        action: "create",
        title: "Create Source",
      },
      {
        path: "/marketing/add_student",
        action: "create",
        title: "Add Student",
      },
      {
        path: "/marketing/all-students",
        action: "view",
        title: "Student List",
      },
      {
        path: "/marketing/update_student",
        action: "update",
        title: "Student List",
      },
      { path: "/AssignLeads", action: "view", title: "Source Assign" },
      { path: "/CounselorCall", action: "view", title: "Call" },
      { path: "/Followup", action: "view", title: "Follow ups" },
    ],
  },
  {
    key: "library_management",
    title: "Library Management",
    label: "Library",
    subtitle: "Library shifts, seats, students and fees",
    icon: "ri-book-marked-line",
    sidebar: true,
    order: 200,
    defaultPath: "/library/dashboard",
    paths: [
      "/library/dashboard",
      "/libraries",
      "/library/shifts",
      "/library/seats",
      "/library/students",
      "/library/fees",
      "/library/dues",
    ],
    tabs: [
      tab(
        "library-dashboard",
        "Library Dashboard",
        "ri-book-shelf-line",
        "/library/dashboard",
      ),
      tab("library-libraries", "Libraries", "ri-school-line", "/libraries"),
      tab(
        "library-shifts",
        "Library Shifts",
        "ri-hourglass-line",
        "/library/shifts",
      ),
      tab(
        "library-seats",
        "Library Seats",
        "ri-armchair-line",
        "/library/seats",
      ),
      tab(
        "library-students",
        "Library Students",
        "ri-group-line",
        "/library/students",
        [
          "/library/students",
          "/library/student/add",
          "/library/student/profile",
          "/library/student/id-card",
        ],
      ),
      tab("library-fees", "Collect Fees", "ri-receipt-line", "/library/fees", [
        "/library/fees",
        "/library/fees/deposit",
        "/library/fee_receipt",
      ]),
      tab("library-dues", "Dues", "ri-error-warning-line", "/library/dues"),
    ],
    routes: [
      {
        path: "/library/dashboard",
        action: "view",
        title: "Library Dashboard",
      },
      { path: "/libraries", action: "view", title: "Libraries" },
      { path: "/library/shifts", action: "view", title: "Library Shifts" },
      { path: "/library/seats", action: "view", title: "Library Seats" },
      { path: "/library/students", action: "view", title: "Library Students" },
      {
        path: "/library/student/add",
        action: "create",
        title: "Library Students",
      },
      {
        path: "/library/student/profile",
        action: "view",
        title: "Library Students",
      },
      {
        path: "/library/student/id-card",
        action: "view",
        title: "Library Students",
      },
      { path: "/library/fees", action: "view", title: "Collect Fees" },
      { path: "/library/fees/deposit", action: "view", title: "Collect Fees" },
      { path: "/library/fee_receipt", action: "view", title: "Collect Fees" },
      { path: "/library/dues", action: "view", title: "Dues" },
      {
        path: "/library/fees/pay",
        action: "create",
        title: "Library Fee Payment",
      },
      { path: "/library/expenses", action: "view", title: "Library Expenses" },
      {
        path: "/library/expense/category",
        action: "view",
        title: "Library Expense Categories",
      },
      { path: "/library/reports", action: "report", title: "Library Reports" },
    ],
  },

  {
    key: "branch_management",
    title: "Branch Management",
    label: "Branch",
    subtitle: "Branch management and student transfer",
    icon: "ri-building-line",
    sidebar: true,
    order: 209,
    defaultPath: "/student/transfer/branch",

    paths: [
      // "/branch/add",
      "/all/branch/list",
      // "/student/transfer/branch",
      // "/student/transfer/list",
    ],

    tabs: [
      // tab("branch-add", "Branch Add", "ri-home-gear-line", "/branch/add"),

      tab("branch-list", "Branch List", "ri-building-line", "/all/branch/list"),

      // tab(
      //   "student-transfer",
      //   "Student Transfer",
      //   "ri-arrow-left-right-line",
      //   "/student/transfer/branch",
      // ),

      // tab(
      //   "student-transfer-list",
      //   "Student Transfer List",
      //   "ri-file-list-3-line",
      //   "/student/transfer/list",
      // ),
    ],

    routes: [
      // { path: "/branch/add", action: "create" },
      { path: "/all/branch/list", action: "view" },
      // { path: "/student/transfer/branch", action: "view" },
      // { path: "/student/transfer/list", action: "view" },
    ],
  },

  {
    key: "settings_management",
    title: "Settings & Configuration",
    label: "Settings",
    subtitle: "School settings, profile, integrations and help",
    icon: "ri-settings-3-line",
    sidebar: true,
    order: 210,
    defaultPath: "/settings",
    paths: [
      "/settings",
      "/settings/addition/details",
      "/app/configuration",
      "/settings/razorpay",
      "/profile",
      "/user/help",
    ],
    tabs: [
      tab("settings-business", "Business", "ri-home-gear-line", "/settings"),
      tab(
        "settings-additional",
        "Addition Details",
        "ri-settings-2-line",
        "/settings/addition/details",
      ),
      tab(
        "settings-config",
        "Configuration",
        "ri-tools-fill",
        "/app/configuration",
      ),
      tab(
        "settings-razorpay",
        "Razorpay Settings",
        "ri-bank-card-line",
        "/settings/razorpay",
      ),
      tab("settings-profile", "Profile", "ri-account-circle-line", "/profile"),
      tab("settings-help", "Help", "ri-question-line", "/user/help", [
        "/user/help",
        "/issue/list",
      ]),
    ],
    routes: [
      { path: "/settings", action: "view", title: "Business" },
      {
        path: "/settings/addition/details",
        action: "view",
        title: "Addition Details",
      },
      { path: "/app/configuration", action: "view", title: "Configuration" },
      {
        path: "/settings/razorpay",
        action: "view",
        title: "Razorpay Settings",
      },
      { path: "/profile", action: "view", title: "Profile" },
      { path: "/user/help", action: "view", title: "Help" },
      { path: "/issue/list", action: "view", title: "Help" },
    ],
  },
];
// =============================================================
// STAFF ROUTE ACCESS — localStorage.staff_access
// Only authenticated Dashboard is universal. Others are granted per route.
// =============================================================
export const MANAGEMENTS_BY_KEY = Object.fromEntries(
  SCAI_MANAGEMENTS.map((management) => [management.key, management]),
);
export const normalizeScaiPath = (value) => {
  const path = String(value || "/")
    .split(/[?#]/)[0]
    .trim();
  return path.replace(/\/+$/, "") || "/";
};
// Parameter segments in the GRANT may match actual path segments.
// Never treat a concrete grant as a wildcard permission.
// export function isRouteMatch(pattern, pathname) {
//   const a = normalizeScaiPath(pattern).split("/");
//   const b = normalizeScaiPath(pathname).split("/");
//   return (
//     a.length === b.length &&
//     a.every((part, i) => part === b[i] || (part.startsWith(":") && b[i] !== ""))
//   );
// }
export function isRouteMatch(pattern, pathname) {
  const normalizedPattern = normalizeScaiPath(pattern);
  const normalizedPath = normalizeScaiPath(pathname);
  const patternParts = normalizedPattern.split("/");
  const pathParts = normalizedPath.split("/");
  if (patternParts.length !== pathParts.length) {
    return false;
  }
  return patternParts.every((part, index) => {
    const currentPart = pathParts[index];
    if (part.startsWith(":")) {
      return currentPart.length > 0;
    }
    return part === currentPart;
  });
}
function readJSON(key, fallback = null) {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw || raw === "undefined" || raw === "null") return fallback;
    return JSON.parse(raw) ?? fallback;
  } catch {
    return fallback;
  }
}
// Legacy Super Admin role remains exempt, as in the existing application.
// Do not use this client-side flag to authorize backend API calls.
export function getNavigationPermissions() {
  // Super Admin role is stored in user_data.
  const userData = readJSON("user_data", {});
  const isSuperAdmin = String(userData?.role_id ?? "") === "1";
  // Stored format: [{ frontend_url, status, ... }, ...]
  const raw = readJSON("staff_access", []);
  const entries = Array.isArray(raw)
    ? raw
    : Array.isArray(raw?.data)
      ? raw.data
      : Array.isArray(raw?.permissions)
        ? raw.permissions
        : [];
  const allowedPaths = [
    ...new Set(
      entries
        .filter(
          (entry) =>
            entry &&
            typeof entry === "object" &&
            String(entry.status ?? "1") === "1",
        )
        .map((entry) => entry.frontend_url)
        .filter((path) => typeof path === "string" && path.startsWith("/"))
        .map(normalizeScaiPath),
    ),
  ];
  return {
    isSuperAdmin,
    allowedPaths,
    source: "staff_access",
  };
}
export function hasManagementRouteAccess(
  routePath,
  access = getNavigationPermissions(),
) {
  if (typeof routePath !== "string" || !routePath.startsWith("/")) return false;
  if (normalizeScaiPath(routePath) === "/") return true;
  if (access.isSuperAdmin) return true;
  return access.allowedPaths.some((grant) => isRouteMatch(grant, routePath));
}
export function getManagement(key) {
  return MANAGEMENTS_BY_KEY[key] || null;
}
export function isTabActive(item, pathname) {
  return (item.activePaths || [item.path]).some((p) =>
    isRouteMatch(p, pathname),
  );
}
// Backward-compatible signature for current Navbar/Sidebar.
export function getManagementTabs(key, _hasAccess) {
  const management = getManagement(key);
  if (!management) return [];
  const access = getNavigationPermissions();
  return (management.tabs || []).reduce((out, item) => {
    if (Array.isArray(item.subItems) && item.subItems.length) {
      const children = item.subItems.filter((child) =>
        hasManagementRouteAccess(child.accessPath || child.path, access),
      );
      if (children.length)
        out.push({ ...item, path: children[0].path, subItems: children });
      return out;
    }
    // Only show a tab if the actual tab destination has access, not merely
    // a secondary Add/Edit route. This prevents misleading forbidden links.
    if (hasManagementRouteAccess(item.accessPath || item.path, access)) {
      if (!item.external || item.externalUrl) out.push({ ...item });
    }
    return out;
  }, []);
}
// Keep route detection based on the complete catalog, even when hidden.
export function getManagementForPath(pathname) {
  return (
    SCAI_MANAGEMENTS.find(
      (management) =>
        (management.routes || []).some((r) => isRouteMatch(r.path, pathname)) ||
        (management.tabs || []).some((t) => isTabActive(t, pathname)),
    ) || null
  );
}
// export function getSidebarManagements(_hasAccess) {
//   return SCAI_MANAGEMENTS.filter((management) => management.sidebar !== false)
//     .map((management) => {
//       const tabs = getManagementTabs(management.key);
//       if (!tabs.length) return null;
//       return {
//         ...management,
//         path: tabs[0].path,
//         defaultPath: tabs[0].path,
//         tabs,
//       };
//     })
//     .filter(Boolean)
//     .sort((a, b) => (a.order || 0) - (b.order || 0));
// }

export function getSidebarManagements(_hasAccess) {
  const client = readJSON("client", {});

  return SCAI_MANAGEMENTS.filter((management) => {
    if (management.sidebar === false) return false;
    if (management.key === "branch_management") {
      return String(client?.is_main_branch) === "1";
    }

    return true;
  })
    .map((management) => {
      const tabs = getManagementTabs(management.key);
      if (!tabs.length) return null;

      return {
        ...management,
        path: tabs[0].path,
        defaultPath: tabs[0].path,
        tabs,
      };
    })
    .filter(Boolean)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
}

export function debugManagementAccess(key, _hasAccess) {
  const management = getManagement(key);
  if (!management) return { key, error: "Management not found" };
  const access = getNavigationPermissions();
  const visibleTabs = getManagementTabs(key);
  return {
    key,
    source: access.source,
    isSuperAdmin: access.isSuperAdmin,
    sidebarVisible: getSidebarManagements().some((m) => m.key === key),
    tabs: management.tabs.map((tab) => ({
      path: tab.path,
      visible: visibleTabs.some((t) => t.id === tab.id),
    })),
    routes: management.routes.map((route) => ({
      path: route.path,
      allowed: hasManagementRouteAccess(route.path, access),
    })),
  };
}
// export function getPermissionCatalog() {
//   return SCAI_MANAGEMENTS .map((management) => ({
//     key: management.key,
//     title: management.title,
//     icon: management.icon,
//     permissions: management.routes.map((route) => ({
//       path: route.path,
//       action: route.action,
//       title:
//         route.title ||
//         management.tabs.find((t) => t.path === route.path)?.title ||
//         route.path,
//     })),
//   }));
// }

export function getPermissionCatalog() {
  const client = readJSON("client", {});

  return SCAI_MANAGEMENTS.filter((management) => {
    if (management.key === "branch_management") {
      return String(client?.is_main_branch) === "1";
    }

    return true;
  }).map((management) => ({
    key: management.key,
    title: management.title,
    icon: management.icon,
    permissions: management.routes.map((route) => ({
      path: route.path,
      action: route.action,
      title:
        route.title ||
        management.tabs.find((t) => t.path === route.path)?.title ||
        route.path,
    })),
  }));
}

export const getTabs = getManagementTabs;
export const getSidebar = getSidebarManagements;
